import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { ModalShell } from '@/components/ui/ModalShell'
import { useModalStore } from '@/hooks/useModalStore'
import { useSettingsStore } from '@/stores/settingsStore'
import logoDark from '@/assets/logo_dark.png'
import logoLight from '@/assets/logo_light.png'
import { APP_NAME, SYSTEM_VERSION } from '@/config/appInfo'

function LinkedInIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

function GitHubIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.373 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.49 11.49 0 0112 5.801c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.302 24 12c0-6.627-5.373-12-12-12z" />
    </svg>
  )
}

/**
 * A stack vive aqui, e nao no i18n, porque nome de biblioteca nao se traduz —
 * e assim ha um lugar so para atualizar quando a pilha mudar.
 */
const STACK = {
  front: ['React 18', 'TypeScript', 'Vite', 'Tailwind CSS', 'Zustand', 'React Router', 'i18next', 'Vitest'],
  back: ['Django 6', 'Django REST Framework', 'Channels (WebSocket)', 'PostgreSQL', 'Redis', 'SimpleJWT', 'ReportLab', 'pytest'],
} as const

function StackRow({ label, items }: { label: string; items: readonly string[] }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--text-dim)' }}>
        {label}
      </span>
      <div className="flex flex-wrap items-center justify-center gap-1">
        {items.map((item) => (
          <span
            key={item}
            className="rounded-md border px-1.5 py-[2px] text-[9px] leading-none"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  )
}

export function AboutModal() {
  const { t } = useTranslation()
  const open = useModalStore((state) => state.aboutOpen)
  const close = useModalStore((state) => state.closeAbout)
  const resolvedTheme = useSettingsStore((s) => s.resolvedTheme)

  return (
    <ModalShell
      open={open}
      title={t('about.title')}
      widthClassName="max-w-[480px]"
      onClose={close}
      footer={(
        <Button variant="primary" onClick={close}>
          {t('common.close')}
        </Button>
      )}
    >
      <div className="flex flex-col items-center gap-2 px-1 py-2 text-center">
        <img src={resolvedTheme === 'dark' ? logoDark : logoLight} alt="MorphERD" className="mb-2 h-auto w-full max-w-[256px]" />

        <div className="flex items-center justify-center gap-2">
          <h3 className="text-base font-bold">{APP_NAME}</h3>
          <span
            className="rounded-lg border px-2 py-[1px] text-[10px]"
            style={{ borderColor: 'rgba(99,102,241,.25)', background: 'rgba(99,102,241,.12)', color: 'var(--accent)' }}
          >
            v{SYSTEM_VERSION}
          </span>
        </div>
        <p className="max-w-[420px] text-[11px] leading-5" style={{ color: 'var(--text-muted)' }}>
          {t('about.projectDesc')}
        </p>

        <div className="mt-2 flex flex-wrap items-center justify-center gap-2.5">
          <a
            href="https://www.linkedin.com/company/morpherd/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border px-3.5 py-[7px] text-[11px] font-bold transition-colors"
            style={{ borderColor: 'rgba(99,102,241,.4)', background: 'rgba(99,102,241,.1)', color: 'var(--accent)' }}
          >
            <LinkedInIcon />
            <span>{t('about.companyLinkedIn')}</span>
          </a>
        </div>

        <div className="my-2 h-px w-full" style={{ background: 'var(--border)' }} />

        <div className="flex w-full flex-col gap-2.5">
          <StackRow label={t('about.stackFront', 'Front-end')} items={STACK.front} />
          <StackRow label={t('about.stackBack', 'Back-end')} items={STACK.back} />
        </div>

        <div className="my-2 h-px w-full" style={{ background: 'var(--border)' }} />

        <div className="flex flex-col items-center gap-1">
          <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-dim)' }}>
            {t('about.founder')}
          </span>
          <h4 className="text-[13px] font-bold">Francisco Lucas Lima Sena</h4>
          <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            {t('about.role')} · {t('about.spec')}
          </p>
          <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
            <a
              href="https://linkedin.com/in/flucassena"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-[6px] text-[10px] font-semibold transition-colors"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
            >
              <LinkedInIcon />
              <span>LinkedIn</span>
            </a>
            <a
              href="https://github.com/flucassena"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-[6px] text-[10px] font-semibold transition-colors"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
            >
              <GitHubIcon />
              <span>GitHub</span>
            </a>
          </div>
        </div>
      </div>
    </ModalShell>
  )
}
